/**
 * @author      matthew@zumper.com (Matthew Talamini)
 * @copyright   Copyright (c) 2024, Zumper
 * @description updateAmenities
 */

import type { BuildingAmenity, ListingAmenity } from '@zcode/enums'
import type { Filters, Listable } from '@zcode/state-type'
import {
  AFR_AMENITIES_PRIORITIES,
  CHEAP_AMENITIES_PRIORITIES,
  CHEAP_AMENITIES_PRIORITIES_HFR,
  FURNISHED_AMENITIES_PRIORITIES,
  FURNISHED_AMENITIES_PRIORITIES_HFR,
  HFR_AMENITIES_PRIORITIES,
  LUXURY_AMENITIES_PRIORITIES,
  LUXURY_AMENITIES_PRIORITIES_HFR,
  PET_FRIENDLY_AMENITIES_PRIORITIES,
  PET_FRIENDLY_AMENITIES_PRIORITIES_HFR,
  UTILITIES_INCLUDED_AMENITIES_PRIORITIES,
  UTILITIES_INCLUDED_AMENITIES_PRIORITIES_HFR,
} from './constants'

const MAP_FILTERS_PATH_TO_INSERTED_AMENITY: { [key: string]: string } = {
  '/pet-friendly': 'Dogs and Cats OK',
  '/dog-friendly': 'Dogs OK',
  '/cat-friendly': 'Cats OK',
  '/cheap': 'Affordable',
  '/luxury': 'Luxury',
  '/short-term': 'Short Term',
  '/move-in-special': 'Rent Special',
  '/utilities-included': 'All Bills Paid',
  '/furnished': 'Furnished',
  '/low-income': 'Income Restricted',
}

export interface UpdateAmenities {
  entity: Partial<Pick<Listable, 'amenities' | 'buildingAmenities' | 'combinedAmenities'>>
  filters: Filters
  filtersPath: string
  isHFR: boolean
  limit?: number
}

const getPrioritiesArray = (filtersPath: string, isHFR: boolean) => {
  switch (filtersPath) {
    case '/pet-friendly':
      return isHFR ? PET_FRIENDLY_AMENITIES_PRIORITIES_HFR : PET_FRIENDLY_AMENITIES_PRIORITIES
    case '/luxury':
      return isHFR ? LUXURY_AMENITIES_PRIORITIES_HFR : LUXURY_AMENITIES_PRIORITIES
    case '/cheap':
      return isHFR ? CHEAP_AMENITIES_PRIORITIES_HFR : CHEAP_AMENITIES_PRIORITIES
    case '/utilities-included':
      return isHFR
        ? UTILITIES_INCLUDED_AMENITIES_PRIORITIES_HFR
        : UTILITIES_INCLUDED_AMENITIES_PRIORITIES
    case '/furnished':
      return isHFR ? FURNISHED_AMENITIES_PRIORITIES_HFR : FURNISHED_AMENITIES_PRIORITIES
    default:
      return isHFR ? HFR_AMENITIES_PRIORITIES : AFR_AMENITIES_PRIORITIES
  }
}

export const updateAmenities = ({
  entity,
  filters,
  filtersPath,
  isHFR,
  limit: limitProp,
}: UpdateAmenities) => {
  const verticalAmenityToInsert = MAP_FILTERS_PATH_TO_INSERTED_AMENITY?.[filtersPath]
  const { cats, dogs, listingAmenities, buildingAmenities } = filters
  const { amenities: entityListingAmenities, buildingAmenities: entityBuildingAmenities } = entity
  const amenitiesInOrder: string[] = []
  let isPetIncluded =
    filtersPath === '/pet-friendly' ||
    filtersPath === '/dog-friendly' ||
    filtersPath === '/cat-friendly'

  const verticalAmenities = verticalAmenityToInsert ? [verticalAmenityToInsert] : []
  const remainingEntityAmenities = new Set<string>(entity.combinedAmenities)

  const uniqueAmenityKeys = new Set(verticalAmenities)
  if (verticalAmenityToInsert && !isPetIncluded) {
    uniqueAmenityKeys.add(verticalAmenityToInsert)
    remainingEntityAmenities.delete(verticalAmenityToInsert)
  }

  const prioritiesArray = getPrioritiesArray(filtersPath, isHFR)

  const limit = limitProp && verticalAmenityToInsert ? limitProp - 1 : limitProp

  const pushAmenitiesInOrder = ({
    listingAmenities,
    buildingAmenities,
  }: {
    listingAmenities?: readonly ListingAmenity[] | readonly (string | number)[]
    buildingAmenities?: readonly BuildingAmenity[] | readonly (string | number)[]
  }) => {
    if (limit && amenitiesInOrder.length >= limit) {
      return
    }

    const hasType = (type: 'listing' | 'building', amenity: string, id: number) => {
      return (
        !uniqueAmenityKeys.has(amenity) &&
        (type === 'listing' ? listingAmenities?.includes(id) : buildingAmenities?.includes(id))
      )
    }

    const pushAmenity = (amenity: string) => {
      amenitiesInOrder.push(amenity)
      uniqueAmenityKeys.add(amenity)
      remainingEntityAmenities.delete(amenity)
    }

    for (const item of prioritiesArray) {
      if (limit && amenitiesInOrder.length >= limit) {
        return
      }

      const { amenities, amenity, type, id } = item
      if (!amenities?.length && type) {
        if (type === 'pets') {
          if (id === 'dogs' && dogs && !isPetIncluded) {
            amenitiesInOrder.push(amenity)
            isPetIncluded = true
          } else if (id === 'cats' && cats && !isPetIncluded) {
            amenitiesInOrder.push(amenity)
            isPetIncluded = true
          }
        } else if (hasType(type, amenity, id)) {
          pushAmenity(amenity)
        }
      } else {
        let hasOneOfGroup = false
        for (const { amenity, type, id } of amenities) {
          if (!hasOneOfGroup) {
            if (hasType(type, amenity, id)) {
              pushAmenity(amenity)
              hasOneOfGroup = true
            }
          } else {
            uniqueAmenityKeys.add(amenity)
            remainingEntityAmenities.delete(amenity)
          }
        }
      }
    }
  }

  // For selected amenities from filters
  pushAmenitiesInOrder({
    listingAmenities,
    buildingAmenities,
  })

  // For amenities in listing/building
  pushAmenitiesInOrder({
    listingAmenities: entityListingAmenities,
    buildingAmenities: entityBuildingAmenities,
  })

  const amenities = [...verticalAmenities, ...amenitiesInOrder, ...remainingEntityAmenities].map(
    (amenity) => sentenceCase(amenity)
  )

  return limitProp ? amenities.slice(0, limitProp) : amenities
}

const sentenceCase = (txt: string) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
